import React from 'react';
import { connect } from 'react-redux';
import anime from 'animejs';
import _ from 'lodash';
import LoadingDots from '../../../components/LoadingDots';
import ReportFilter from '../../../containers/ManagerDashboard/ReportFilter';
import ProjectReportFilters from '../../../containers/ManagerDashboard/ProjectReport/ProjectReportFilters';
import BaseComponent from '../../../baseClasses/BaseComponent';
import CollectionDataSource from '../../../dao/DataSource/CollectionDataSource';
import CheckBox from '../../../components/CheckBox';
import SearchBar from '../../../components/SearchBar/SearchBar';
import filterBlueIcon from '../../../assets/images/project-filter-icon-blue.svg';
import { teamAssignedProjects } from '../../../helpers/teamAssignedProjects';
import './SelectTeamProjects.scss';

const projectDescriptor = { projects: { project_manager: { additionalFields: ['profile-image'] }, project_type: {} } };

function syncSearchStringToCollection(dataSource, searchTerm) {
  if (!dataSource) {
    return;
  }
  const trimmed = (searchTerm || '').trim();
  dataSource.searchString = trimmed ? trimmed : null;
}

export class SelectTeamProjects extends BaseComponent {
  constructor(props) {
    super(props);

    this.state = {
      menu: null, 
      editing: false,
      loadingProjects: true,
      loadingMoreProjects: false,
      projectSearchTerm: '',
    };
    this.isFilterEmpty = this.isFilterEmpty.bind(this);
    this.getPMName = this.getPMName.bind(this);
    this.handleProjectSearchChange = this.handleProjectSearchChange.bind(this);
    this.accent = this.props.currentCompany.accent_color ? this.props.currentCompany.accent_color : '#CD03A';
  }

  handleProjectsRetrieve(projects, action, acData, fetchedData) {
    const { team } = this.props;
    if (action == 'scrolling') {
      if (projects.ids.length >= projects.totalProjects) {
        this.infiniteDone = true;
      }
      this.setState({ loadingMoreProjects: false });
      this.scrollinProgress = false;
    }
    else if (action != 'prefetch' || projects.projects.length > 0) {
      this.setState({ loadingProjects: false });
    }
    
    const assigned = teamAssignedProjects(team);
    let selectAllProjects = false;
    if (assigned.length && projects.totalProjects == assigned.length) {
      selectAllProjects = true;
    }
    this.setState({ projects, selectAllProjects });
  }
  
  handleResetProjects(team) {
    teamAssignedProjects(team).forEach(project => {
      this.handleSelectProject(project, true);
    });
  }

  componentDidMount() {
    const { team, resetProjects, marketsChanged } = this.props;
    let selectedProjects = {};
    teamAssignedProjects(team).forEach(project => {
      selectedProjects[project.id] = true;
    });
    this.setState({ team: _.cloneDeep(team), selectedProjects });

    if (resetProjects && marketsChanged) {
      this.handleResetProjects(team);
    }

    this.projectsDataSource = new CollectionDataSource('ProjectsCollection', 'Project', projectDescriptor, this.handleProjectsRetrieve,
      'TeamProjects_' + team.name, 7, null, this.props.currentUser.id, this.props.currentCompany.id, team.name, null, null, true, team.markets);
    syncSearchStringToCollection(this.projectsDataSource, this.state.projectSearchTerm);
    this.projectsDataSource.current('', resetProjects);
    if (!this.isFilterEmpty(this.projectsDataSource.filter)) {
      this.setState({ filterActive: true });
    }
  }

  componentWillUnmount() {
    if (this.projectsDataSource) {
      this.projectsDataSource.release();
      this.projectsDataSource = null;
    }
  }

  componentDidUpdate(prevProps) {
    const { team, visible, resetProjects } = this.props;
    if (visible != prevProps.visible && visible) {
      let selectedProjects = resetProjects ? {} : this.state.selectedProjects;
      const assignedVisible = teamAssignedProjects(team);
      if (assignedVisible.length) {
        assignedVisible.forEach(project => {
          selectedProjects[project.id] = true;
        });

        if (this.props.handleManageTeamProjects) {
          let projectArray = [];
          for (let sp in selectedProjects) {
            if (sp && selectedProjects[sp]) {
              projectArray.push(+sp);
            }
          }
          this.props.handleManageTeamProjects(projectArray, false);
        }
      }
      this.setState({ team: _.cloneDeep(team), selectedProjects }, () => {
        if (resetProjects != this.resetProjects) {
          this.resetProjects = resetProjects;
          if (resetProjects) {
            if (this.props.handleResetProjects) {
              this.props.handleResetProjects();
            }
            this.projectsDataSource = new CollectionDataSource('ProjectsCollection', 'Project', projectDescriptor, this.handleProjectsRetrieve,
              'TeamProjects_' + team.name, 7, null, this.props.currentUser.id, this.props.currentCompany.id, team.name, null, null, true, team.markets);
            syncSearchStringToCollection(this.projectsDataSource, this.state.projectSearchTerm);
            this.projectsDataSource.current('', resetProjects);
            if (!this.isFilterEmpty(this.projectsDataSource.filter)) {
              this.setState({ filterActive: true });
            }
            if (this.props.marketsChanged) {
              this.handleResetProjects(team);
            }
          }
        }
      });
    }
    if (resetProjects != this.resetProjects && !resetProjects) {
      this.resetProjects = resetProjects;
    }
    // Parent passes a new `team` object every render (e.g. teamForWizardChild); only reset the
    // project collection when identity, markets, or assigned projects actually change — not on name/logo-only edits.
    const prevTeam = prevProps.team || {};
    const idChanged = prevTeam.id !== team.id;
    const marketsChanged = !_.isEqual(
      (prevTeam.markets || []).map(m => { return m.id; }).sort(),
      (team.markets || []).map(m => { return m.id; }).sort(),
    );
    const prevProjectIds = teamAssignedProjects(prevTeam).map(p => { return p.id; }).sort().join(',');
    const nextProjectIds = teamAssignedProjects(team).map(p => { return p.id; }).sort().join(',');
    const projectsOnTeamChanged = prevProjectIds !== nextProjectIds;
    if (idChanged || marketsChanged || projectsOnTeamChanged) {
      if (this.projectsDataSource) {
        this.projectsDataSource.release();
      }

      let selectedProjects = {};
      teamAssignedProjects(team).forEach(project => {
        selectedProjects[project.id] = true;
      });

      this.setState({ team: _.cloneDeep(team), selectedProjects });
      this.projectsDataSource = new CollectionDataSource('ProjectsCollection', 'Project', projectDescriptor, this.handleProjectsRetrieve,
        'TeamProjects_' + team.name, 7, null, this.props.currentUser.id, this.props.currentCompany.id, team.name, null, null, true, team.markets);
      syncSearchStringToCollection(this.projectsDataSource, this.state.projectSearchTerm);
      this.projectsDataSource.current();
      if (!this.isFilterEmpty(this.projectsDataSource.filter)) {
        this.setState({ filterActive: true });
      }
    }
  }

  async handleFilterActive() {
    if (!this.filterInProgress) {
      this.filterInProgress = true;
      const filterDisplayed = !this.state.filterDisplayed;
      this.setState({ filterDisplayed });
      if (!this.state.filterDisplayed) {
        await anime({
          targets: '.TeamProjectsReportFilter',
          height: 184,
          easing: 'easeInOutCubic',
          duration: 500,
          complete: () => {
            this.filterInProgress = false;
            document.querySelector('.TeamProjectsReportFilter').style.overflow = 'inherit';
            document.querySelector('.MapProjectReportFilters').style.overflow = 'inherit';
          },

        });
      }
      else {
        await anime({
          targets: '.TeamProjectsReportFilter',
          height: 0,
          easing: 'easeInOutCubic',
          duration: 500,
          begin: () => {
            document.querySelector('.TeamProjectsReportFilter').style.overflow = 'hidden';
            document.querySelector('.MapProjectReportFilters').style.overflow = 'hidden';
          },
          complete: () => {
            this.filterInProgress = false;
          },
        });
      }

      let filter = this.projectsDataSource.filter;
      filter.filterDisplayed = filterDisplayed;
      this.projectsDataSource.filter = filter;
    }
  }

  handleFilterChange(filter) {
    if (!this.isFilterEmpty(filter)) {
      this.setState({ filterActive: true });
    }
    else {
      this.setState({ filterActive: false });
    }
    this.infiniteDone = false;
    this.animateIn = true;
    this.firstTime = false;
    this.projectsDataSource.filter = filter;
    this.projectsDataSource.page = 0;
    this.projectsDataSource.current();
  }

  getPMName(pm, initials) {
    if (initials) {
      return (pm.first_name ? pm.first_name.substr(0, 1) : '') + (pm.last_name ? pm.last_name.substr(0, 1) : '');
    }
    else {
      return pm.name ? pm.name : ((pm.first_name ? pm.first_name + ' ' : '') + (pm.last_name ? pm.last_name : ''));
    }
  }

  handleProjectSearchChange(searchTerm) {
    this.setState({ projectSearchTerm: searchTerm, loadingProjects: true });
    if (!this.projectsDataSource) {
      return;
    }
    this.infiniteDone = false;
    syncSearchStringToCollection(this.projectsDataSource, searchTerm);
    this.projectsDataSource.page = 0;
    this.projectsDataSource.current();
  }

  handleCreateProjectList() {
    if (this.state.projects) {
      const projects = this.state.projects.projects;

      return projects.map(project => {

        const projectManager = project.project_manager;
        let userName = projectManager ? (projectManager.name ? projectManager.name : projectManager.first_name + ' ' + projectManager.last_name) : 'None Assigned';
        let profileImage = projectManager && projectManager.profile_image;
        let userImage = null;
        if (profileImage) {
          userImage = <img className="profile-image" alt="User" src={profileImage} />;
        }
        else {
          const initials = !projectManager ? 'NA' : this.getPMName(projectManager, true);
          userImage = (
            <div className="default-user-image-container">
              <div className="default-user-image" />
              <div className="user-initials">{initials}</div>
            </div>
          );
        }
    
        return <div key={project.id} className="select-projects-line">
          <div className="checkbox">
            <CheckBox key="checkbox" toggleCheckBox={() => this.handleSelectProject(project)} medium={true}
              checked={this.state.selectedProjects && this.state.selectedProjects[project.id]} />
          </div>
          <div className='text one'>{project.name}</div>
          <div className='text two'>{project.guid}</div>
          <div className="user-image">{userImage}</div>
          <div className='text three'>{userName}</div>
          <div className='text four'>{!project.project_type ? 'none' : project.project_type.name}</div>
        </div>;
      });
    }
    else {
      return null;
    }
  }

  handleSelectProject(project, deselectOnly) {
    let { selectedProjects } = this.state;
    if (!selectedProjects) {
      selectedProjects = {};
    }
    if (selectedProjects[project.id] || deselectOnly) {
      selectedProjects[project.id] = false;
    }
    else {
      selectedProjects[project.id] = true;
    }
    let allUnselected = true;
    for (let sp in selectedProjects) {
      if (sp && selectedProjects[sp]) {
        allUnselected = false;
        break;
      }
    }
    if (allUnselected) {
      this.setState({ selectAllProjects: false });
    }
    this.setState({ selectedProjects });
    if (this.props.handleManageTeamProjects) {
      let projectArray = [];
      for (let sp in selectedProjects) {
        if (sp && selectedProjects[sp]) {
          projectArray.push(+sp);
        }
      }
      this.props.handleManageTeamProjects(projectArray, false);
    }
  }

  handleSelectAllProjects() {
    let { selectAllProjects, selectedProjects, projects } = this.state;
    selectAllProjects = !selectAllProjects;

    if (!selectedProjects) {
      selectedProjects = {};
    }
    if (projects) {
      projects.projects.forEach(project =>{
        selectedProjects[project.id] = selectAllProjects;
      });
    }
    this.setState({ selectAllProjects, selectedProjects });

    if (this.props.handleManageTeamProjects) {
      let projectArray = [];
      for (let sp in selectedProjects) {
        if (sp && selectedProjects[sp]) {
          projectArray.push(+sp);
        }
      }
      const filter = this.projectsDataSource.filter || {};

      this.props.handleManageTeamProjects(projectArray, selectAllProjects, filter.project_managers, filter.project_types);
    }

  }

  isFilterEmpty(filter) {
    return _.isEmpty(filter) || ((!filter.project_types || filter.project_types.length == 0) &&
      (!filter.project_managers || filter.project_managers.length == 0));
  }

  handleProjectManagerSelection(project_managers) {
    const filter = this.projectsDataSource.filter || {};
    filter.project_managers = project_managers;
    
    this.handleFilterChange(filter);
  }

  handleProjectTypeSelection(project_types) {
    const filter = this.projectsDataSource.filter || {};
    filter.project_types = project_types;

    this.handleFilterChange(filter);
  }

  handleScrolling() {
    const { projects } = this.state;
    const listRect = this.list.getBoundingClientRect();
    const scrollRect = this.scroller.getBoundingClientRect();
    if (!this.infiniteDone && listRect.bottom - 19 <= scrollRect.bottom && !this.scrollinProgress && projects.ids.length < projects.totalProjects) {
      this.scrollinProgress = true;
      this.setState({ loadingMoreProjects: true });
      this.projectsDataSource.next('scrolling');
    }
  }

  render() {
    let { team } = this.state;
    if (team == undefined) {
      return null;
    }

    const filter = this.projectsDataSource ? this.projectsDataSource.filter : {};
    const projectSearchFilter = (
      <div className='project-search-filter-container'>
        <ReportFilter altClassName={'TeamProjectsReportFilter'}
          isVertical={true}
        >
          <ProjectReportFilters altClassName={'MapProjectReportFilters'}
            handleProjectTypeSelection={this.handleProjectTypeSelection}
            handleProjectManagerSelection={this.handleProjectManagerSelection}
            selectedFilters={filter}
            isDashboard={true}
            dontIncludeStatus={true}
            handleFilterChange={this.handleFilterChange}
            isVertical={true}
            showArchived={null}
            markets={team.markets}
          />
        </ReportFilter>
      </div>
    );

    return (
      <div id={'select-team-projects' + this.props.team.id} className="select-team-projects" style={{ display: this.props.visible ? 'block' : 'none' }}>
        <div className='filter-header'>
          <div className='caption'>Assign Projects</div>
          <div className="team-project-filter">
            {projectSearchFilter}
            <div
              className={'project-filter' + (this.state.filterDisplayed ? ' map-filter__active' : '')}
              onClick={this.handleFilterActive}
            >
              <img className='filter-icon' src={filterBlueIcon} alt="Filter Report" />
              {this.state.filterActive && <div className='filter-pip'></div>}
              <p className='filter-text'>{this.textMap.filter.toUpperCase()}</p>
              {
                this.state.filterDisplayed && <div className='tab-indicator'></div>
              }
            </div>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="project-search-container">
          <SearchBar
            placeholder="Search projects by name, manager, or type..."
            onSearchChange={this.handleProjectSearchChange}
            width="100%"
            height="32px"
            debounceMs={300}
          />
        </div>
        
        <div className='select-team-projects-header'>
          <div className="checkbox">
            <CheckBox key="checkbox" toggleCheckBox={this.handleSelectAllProjects} medium={true}
              checked={this.state.selectAllProjects} />
          </div>
          <div className='label one'>PROJECT NAME</div>
          <div className='label two'>PROJECT ID</div>
          <div className='label three'>PROJECT MANAGER</div>
          <div className='label four'>PROJECT TYPE</div>
        </div>
        <div className='select-projects-scroller'
          ref={element => {
            this.scroller = element;
            if (this.scroller) {
              this.scroller.addEventListener('scroll', this.handleScrolling);
            }
          }}
        >
          {
            this.state.loadingProjects && <div className='projects-loading-more-indicator'><LoadingDots accent={this.accent} /></div>
          }
          <div className='select-projects-container' ref={(element) => { this.list = element; }}>
            {this.handleCreateProjectList()}
          </div>
        </div>
        {
          this.state.loadingMoreProjects && <div className='projects-loading-more-indicator bottom'><LoadingDots accent={this.accent} /></div>
        }
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    currentUser: state.new_users.currentUser,
    permissions: state.new_users.permissions,
    currentCompany: state.new_companies.currentCompany,
  };
};

export default connect(mapStateToProps)(SelectTeamProjects);



// WEBPACK FOOTER //
// ./src/components/TeamPopup/SelectTeamProjects/index.js